import React, { useState } from 'react';
import { Star, Send } from 'lucide-react';

interface ReviewFormProps {
  onSubmit: (rating: number, text: string, visitDate?: string) => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
}

export function ReviewForm({ onSubmit, onCancel, isLoading = false }: ReviewFormProps) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [text, setText] = useState('');
  const [visitDate, setVisitDate] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (rating === 0) {
      setError('Bitte gib eine Bewertung ab');
      return;
    }

    if (text.trim().length < 10) {
      setError('Bitte schreibe mindestens 10 Zeichen');
      return;
    }

    try {
      await onSubmit(rating, text.trim(), visitDate || undefined);
      setRating(0);
      setText('');
      setVisitDate('');
    } catch (err) {
      setError('Fehler beim Speichern der Bewertung');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white border rounded-lg p-4">
      <h4 className="font-medium text-gray-800 mb-4">Deine Bewertung</h4>

      {/* Star Rating */}
      <div className="mb-4">
        <label className="block text-sm text-gray-600 mb-2">Wie hat es dir gefallen?</label>
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(star)}
              onMouseEnter={() => setHoverRating(star)}
              onMouseLeave={() => setHoverRating(0)}
              className="p-1 transition-transform hover:scale-110"
            >
              <Star
                className={`w-8 h-8 transition-colors ${
                  star <= (hoverRating || rating)
                    ? 'text-yellow-400 fill-yellow-400'
                    : 'text-gray-300'
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      {/* Review Text */}
      <div className="mb-4">
        <label className="block text-sm text-gray-600 mb-2">
          Erzähl uns von deinem Besuch
        </label>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Was hat dir besonders gefallen? Wie war die Atmosphäre?"
          rows={4}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
        />
      </div>

      {/* Visit Date (optional) */}
      <div className="mb-4">
        <label className="block text-sm text-gray-600 mb-2">
          Wann warst du dort? (optional)
        </label>
        <input
          type="date"
          value={visitDate}
          onChange={(e) => setVisitDate(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
          className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
        />
      </div>

      {/* Error */}
      {error && (
        <p className="text-red-500 text-sm mb-4">{error}</p>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Abbrechen
          </button>
        )}
        <button
          type="submit"
          disabled={isLoading}
          className="flex-1 py-2 bg-indigo-500 text-white rounded-lg font-medium hover:bg-indigo-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        >
          <Send className="w-4 h-4" />
          {isLoading ? 'Wird gespeichert...' : 'Bewertung abgeben'}
        </button>
      </div>
    </form>
  );
}
